import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import FavoriteButton from './FavoriteButton';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const MyFavoritesList = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    if (user) {
      fetchFavorites();
    }
  }, [user, filter]);

  const fetchFavorites = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`/api/favorites/vendors${params}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setFavorites(data || []);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSnooze = async (vendorId) => {
    try {
      const res = await fetch(`/api/favorites/vendor/${vendorId}/snooze`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ note: 'Snoozed from dashboard' })
      });
      if (res.ok) {
        fetchFavorites();
      }
    } catch (error) {
      console.error('Error snoozing favorite:', error);
    }
  };

  const handleActivate = async (vendorId) => {
    try {
      const res = await fetch(`/api/favorites/vendor/${vendorId}/activate`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.ok) {
        fetchFavorites();
      }
    } catch (error) {
      console.error('Error activating favorite:', error);
    }
  };

  const getVendorName = (favorite) => {
    if (favorite.vendorId) {
      return favorite.vendorId.farmName || 
             favorite.vendorId.hubSettings?.hubName || 
             favorite.vendorId.name || 
             'Vendor';
    }
    return 'Vendor';
  };

  const getVendorSlug = (favorite) => {
    return favorite.vendorId?.slug || favorite.vendorId?._id;
  };

  const getVendorImage = (favorite) => {
    const logo = favorite.vendorId?.logoUrl || 
                 favorite.vendorId?.storefront?.logoUrl ||
                 favorite.vendorId?.storefrontV2?.logoUrl;
    if (logo) return logo;
    return null;
  };

  const getVendorRole = (favorite) => {
    return favorite.vendorId?.role || 'producer';
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      snoozed: 'bg-yellow-100 text-yellow-800',
      inactive: 'bg-gray-100 text-gray-500'
    };
    return badges[status] || badges.inactive;
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Active',
      snoozed: '💤 Snoozed',
      inactive: 'Inactive'
    };
    return labels[status] || status;
  };

  const getStatusTooltip = (status) => {
    const tooltips = {
      active: 'You will receive updates from this vendor',
      snoozed: 'Temporarily paused - you won\'t receive updates',
      inactive: 'You have unfavorited this vendor'
    };
    return tooltips[status] || '';
  };

  const getAvatarColor = (name) => {
    const colors = [
      'bg-red-100 text-red-700',
      'bg-blue-100 text-blue-700',
      'bg-green-100 text-green-700',
      'bg-yellow-100 text-yellow-700',
      'bg-purple-100 text-purple-700',
      'bg-pink-100 text-pink-700',
      'bg-indigo-100 text-indigo-700',
      'bg-teal-100 text-teal-700'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please login to see your favorites</p>
        <Link to="/login" className="text-green-600 hover:underline">Login</Link>
      </div>
    );
  }

  return (
    <div className="my-favorites-list">
      <div className="flex justify-end mb-4">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <option value="active">Active</option>
          <option value="snoozed">Snoozed</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="border rounded-lg p-4 animate-pulse bg-gray-50 h-32" />
          ))}
        </div>
      ) : favorites.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <p className="text-4xl mb-2">🤍</p>
          <p className="text-gray-600">
            {filter === 'active' && 'You haven\'t favorited any active vendors yet'}
            {filter === 'snoozed' && 'You don\'t have any snoozed favorites'}
            {filter === 'inactive' && 'You don\'t have any inactive favorites'}
            {filter === 'all' && 'You haven\'t favorited any vendors yet'}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Browse producers and hubs and click the ❤️ button to save them
          </p>
          <Link to="/producers" className="inline-block mt-4 text-green-600 hover:underline">
            Browse Producers →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {favorites.map((favorite) => {
            const vendorId = favorite.vendorId?._id || favorite.vendorId;
            const vendorName = getVendorName(favorite);
            const vendorImage = getVendorImage(favorite);
            const vendorRole = getVendorRole(favorite);
            const avatarColor = getAvatarColor(vendorName);
            const initials = getInitials(vendorName);
            const isHub = vendorRole === 'hub';
            const isActive = favorite.status === 'active';
            const isSnoozed = favorite.status === 'snoozed';

            return (
              <div key={favorite._id} className="border rounded-lg p-4 hover:shadow-md transition">
                <div className="grid grid-cols-[auto,1fr,auto] gap-3 items-start">
                  {/* Column 1: Logo */}
                  <div className="flex-shrink-0">
                    {vendorImage ? (
                      <img
                        src={vendorImage}
                        alt={vendorName}
                        className="w-16 h-16 rounded-full object-cover bg-gray-100"
                      />
                    ) : (
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold ${avatarColor}`}>
                        {initials}
                      </div>
                    )}
                  </div>
                  
                  {/* Column 2: Name and details */}
                  <div className="min-w-0">
                    <Link 
                      to={`/${isHub ? 'hub' : 'producer'}/${getVendorSlug(favorite)}`}
                      className="font-medium hover:text-green-600 hover:underline truncate block"
                      title={vendorName}
                    >
                      {vendorName}
                    </Link>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span 
                        className={`text-xs px-2 py-0.5 rounded-full inline-block ${getStatusBadge(favorite.status)}`}
                        title={getStatusTooltip(favorite.status)}
                      >
                        {getStatusLabel(favorite.status)}
                      </span>
                      <p className="text-xs text-gray-400">
                        {new Date(favorite.createdAt).toLocaleDateString()}
                      </p>
                      {isActive && (
                        <button
                          onClick={() => handleSnooze(vendorId)}
                          className="text-xs text-yellow-600 hover:text-yellow-800 hover:underline"
                          title="Temporarily pause updates from this vendor"
                        >
                          💤 Snooze
                        </button>
                      )}
                      {isSnoozed && (
                        <button
                          onClick={() => handleActivate(vendorId)}
                          className="text-xs text-green-600 hover:text-green-800 hover:underline"
                          title="Resume updates from this vendor"
                        >
                          ▶️ Activate
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Column 3: Favorite button */}
                  <div className="flex-shrink-0">
                    <FavoriteButton vendorId={vendorId} size="sm" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyFavoritesList;
